import React from "react";
import { ArrowUp, ArrowDown, XCircle, Shield, AlertTriangle, Flame } from "lucide-react";

/**
 * Active position card with live PnL, close / move-SL / panic / revenge actions.
 */
export const PositionPanel = ({
  position,
  currentPrice,
  pnlPct,
  pnlUSD,
  highVolatility,
  onClose,
  onMoveSLBE,
  onPanicSell,
  onRevengeTrade,
}) => {
  if (!position) {
    return (
      <div
        className="rounded-2xl border border-dashed border-white/10 bg-[#0a0b14] p-5 text-center"
        data-testid="sim-position-empty"
      >
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/35">
          Sin posición activa
        </p>
        <p className="mt-2 text-xs text-white/45">
          Abre un Long o Short desde el panel de órdenes.
        </p>
      </div>
    );
  }

  const isLong = position.side === "long";
  const positive = pnlPct >= 0;
  const Icon = isLong ? ArrowUp : ArrowDown;

  return (
    <div
      className={`rounded-2xl border bg-[#0a0b14] p-4 ${
        isLong ? "border-emerald-400/30" : "border-rose-400/30"
      }`}
      data-testid="sim-position-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-lg border ${
              isLong ? "border-emerald-400/40 bg-emerald-400/10" : "border-rose-400/40 bg-rose-400/10"
            }`}
          >
            <Icon
              className={`h-3.5 w-3.5 ${isLong ? "text-emerald-300" : "text-rose-300"}`}
            />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/80">
            {position.side} · {position.sizePct}%
          </span>
        </div>
        <span className="font-mono text-[10px] text-white/40">
          Entry: ${position.entry.toFixed(position.entry < 100 ? 2 : 0)}
        </span>
      </div>

      <div className="mt-4 flex items-baseline gap-3">
        <span
          className={`font-display text-3xl font-black tracking-tighter ${
            positive ? "text-emerald-300" : "text-rose-300"
          }`}
          data-testid="sim-pnl-pct"
          style={{
            animation: `${positive ? "pnl-flash-green" : "pnl-flash-red"} 1.2s infinite`,
          }}
        >
          {positive ? "+" : ""}
          {pnlPct.toFixed(2)}%
        </span>
        <span className={`font-mono text-xs ${positive ? "text-emerald-400/80" : "text-rose-400/80"}`}>
          {positive ? "+" : ""}${pnlUSD.toFixed(2)}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] font-mono">
        <div className="rounded-md border border-white/10 bg-black/30 p-2">
          <p className="uppercase tracking-[0.22em] text-white/35">Price</p>
          <p className="mt-1 text-white/85">${currentPrice?.toFixed(currentPrice < 100 ? 2 : 0)}</p>
        </div>
        <div className="rounded-md border border-rose-400/20 bg-rose-400/[0.05] p-2">
          <p className="uppercase tracking-[0.22em] text-rose-300/70">SL</p>
          <p className="mt-1 text-rose-200">${position.sl.toFixed(position.sl < 100 ? 2 : 0)}</p>
        </div>
        <div className="rounded-md border border-emerald-400/20 bg-emerald-400/[0.05] p-2">
          <p className="uppercase tracking-[0.22em] text-emerald-300/70">TP</p>
          <p className="mt-1 text-emerald-200">${position.tp.toFixed(position.tp < 100 ? 2 : 0)}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onClose}
          data-testid="sim-btn-close"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.04] py-2 text-[11px] font-bold uppercase tracking-wider text-white/85 transition-all hover:bg-white/10"
        >
          <XCircle className="h-3.5 w-3.5" /> Cerrar
        </button>
        <button
          type="button"
          onClick={onMoveSLBE}
          data-testid="sim-btn-move-sl-be"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.06] py-2 text-[11px] font-bold uppercase tracking-wider text-cyan-200 transition-all hover:bg-cyan-400/15"
        >
          <Shield className="h-3.5 w-3.5" /> SL → BE
        </button>
      </div>

      {highVolatility && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onPanicSell}
            data-testid="sim-btn-panic"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-400 bg-rose-400/15 py-2 text-[10px] font-bold uppercase tracking-wider text-rose-200"
            style={{ animation: "pulse-live 1.5s infinite" }}
          >
            <AlertTriangle className="h-3 w-3" /> Panic Sell
          </button>
          <button
            type="button"
            onClick={onRevengeTrade}
            data-testid="sim-btn-revenge"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-400 bg-amber-400/15 py-2 text-[10px] font-bold uppercase tracking-wider text-amber-200"
          >
            <Flame className="h-3 w-3" /> Revenge
          </button>
        </div>
      )}
    </div>
  );
};

export default PositionPanel;
